import { apiRequest } from "../api/client";
import { bcp47 } from "../i18n/strings";

// Speech-to-text through Azure AI Speech, straight from the operator's
// microphone. Compared with the browser's built-in recognizer (Chrome's
// general-purpose cloud model) it:
//   - accepts a PHRASE LIST, so AFA vocabulary from the terminology dictionary
//     is heard correctly instead of "start the session" -> "store the session",
//   - is trained for continuous, long-form speech and handles accents better,
//   - works the same in every browser, and
//   - lets us set how long a pause ends a sentence, so a sentence isn't cut
//     into fragments that get translated without their context.
//
// The Azure key never reaches the browser: the backend hands out a short-lived
// token (GET /speech/token) which we refresh before it expires.

interface SpeechTokenResponse {
  available: boolean;
  reason?: string;
  token?: string;
  region?: string;
  language?: string;
  phrases?: string[];
  expiresInSec?: number;
}

export interface AzureRecognitionHandle {
  stop: () => Promise<void>;
}

export interface AzureRecognitionCallbacks {
  /** Text being heard right now (not final yet) — shown as a "we can hear you" indicator. */
  onInterim: (text: string) => void;
  /** A phrase ended (the recognizer heard a pause). `text` is empty if it heard only noise. */
  onFinal: (text: string) => void;
  /** Recognition stopped by itself (bad key, lost network, ...). The caller decides what to do next. */
  onFatalError: (message: string) => void;
}

/**
 * A pause this long (ms) ends a phrase. Short on purpose: natural pauses are
 * 300-600 ms, and every phrase boundary is a point where translation can start
 * without waiting for the speaker to finish. (Speech with NO pauses is handled
 * separately by UtteranceChunker, which commits chunks from the live hypothesis.)
 */
const SENTENCE_PAUSE_MS = 500;
/** Renew the token well inside its 10-minute life. */
const TOKEN_REFRESH_MS = 8 * 60 * 1000;

/**
 * Starts continuous recognition. Resolves to `null` when the server says Azure
 * speech isn't available (mock mode / no key) so the caller can use the browser
 * recognizer; throws on real failures such as no microphone permission.
 */
export async function startAzureRecognition(
  sessionId: string,
  callbacks: AzureRecognitionCallbacks,
  options: { pauseMs?: number } = {},
): Promise<AzureRecognitionHandle | null> {
  const info = await apiRequest<SpeechTokenResponse>("/speech/token", { query: { sessionId } });
  if (!info.available || !info.token || !info.region) return null;

  const sdk = await import("microsoft-cognitiveservices-speech-sdk");

  const speechConfig = sdk.SpeechConfig.fromAuthorizationToken(info.token, info.region);
  speechConfig.speechRecognitionLanguage = bcp47(info.language ?? "en");
  speechConfig.setProperty(
    sdk.PropertyId.Speech_SegmentationSilenceTimeoutMs,
    String(options.pauseMs ?? SENTENCE_PAUSE_MS),
  );

  const audioConfig = sdk.AudioConfig.fromDefaultMicrophoneInput();
  const recognizer = new sdk.SpeechRecognizer(speechConfig, audioConfig);

  if (info.phrases?.length) {
    sdk.PhraseListGrammar.fromRecognizer(recognizer).addPhrases(info.phrases);
  }

  let stopped = false;

  recognizer.recognizing = (_sender, e) => {
    if (e.result.text) callbacks.onInterim(e.result.text);
  };
  recognizer.recognized = (_sender, e) => {
    callbacks.onInterim("");
    // Always report the end of a segment — with empty text when Azure heard only
    // noise — so the caller can close out the phrase and clear its live line.
    const heard = e.result.reason === sdk.ResultReason.RecognizedSpeech ? e.result.text.trim() : "";
    callbacks.onFinal(heard);
  };
  recognizer.canceled = (_sender, e) => {
    if (stopped || e.reason !== sdk.CancellationReason.Error) return;
    stopped = true;
    let message = e.errorDetails || "Azure speech recognition stopped unexpectedly.";
    if (e.errorCode === sdk.CancellationErrorCode.AuthenticationFailure) {
      message = "Azure rejected the speech credentials. Check AZURE_SPEECH_KEY / AZURE_SPEECH_REGION in backend/.env.";
    } else if (e.errorCode === sdk.CancellationErrorCode.TooManyRequests) {
      message = "Azure Speech is busy or over its quota (the free tier allows one stream at a time).";
    } else if (e.errorCode === sdk.CancellationErrorCode.ConnectionFailure) {
      message = "Lost the connection to Azure Speech. Check the internet connection.";
    }
    callbacks.onFatalError(message);
  };

  const refreshTimer = setInterval(async () => {
    try {
      const fresh = await apiRequest<SpeechTokenResponse>("/speech/token", { query: { sessionId } });
      if (fresh.token) recognizer.authorizationToken = fresh.token;
    } catch {
      // Keep going on the current token; if it expires, `canceled` will report it.
    }
  }, TOKEN_REFRESH_MS);

  await new Promise<void>((resolve, reject) => recognizer.startContinuousRecognitionAsync(resolve, (err) => reject(new Error(String(err)))));

  return {
    stop: async () => {
      stopped = true;
      clearInterval(refreshTimer);
      await new Promise<void>((resolve) =>
        recognizer.stopContinuousRecognitionAsync(
          () => resolve(),
          () => resolve(),
        ),
      );
      recognizer.close();
    },
  };
}
